import { RootState } from "..";

import { createAppSelector, selectBreakoutGroups, selectCourseUsers } from "./base";
import { selectActiveClassSession, selectIsClassSessionVirtual } from "./class-session";
import { selectCurrentClassUser, selectCurrentClassUserList } from "./class-user";
import { selectCourseUserIdToUserIdMap, selectCurrentCourse } from "./course";
import {
  selectIsRecordingAgent,
  selectUserIdsWhoAreVisibleInClass,
  selectUserIdsWhoCanParticipateInCourse,
} from "./permissions";

export const selectClassUsersInVirtualClass = createAppSelector(
  [selectCurrentClassUserList, selectIsClassSessionVirtual],
  (classUsers, isClassSessionVirtual) =>
    isClassSessionVirtual ? classUsers.filter((cu) => cu.is_in_class_session) : [],
);

export const selectUserIdsInVirtualClass = createAppSelector(
  [selectClassUsersInVirtualClass, selectCourseUsers],
  (classUsers, courseUsers) =>
    classUsers
      .map((classUser) => courseUsers.find((cu) => cu.id === classUser.course_user_id)?.user_id)
      .filter((userId) => !!userId),
);

export const selectVirtualClassAvService = createAppSelector(
  [selectCurrentCourse],
  (currentCourse) => {
    if (!currentCourse) {
      return null;
    }
    return currentCourse.av_service;
  },
);

export const selectVirtualClassLicodeServerUrl = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => {
    if (!currentClassSession) {
      return null;
    }
    return currentClassSession.licode_server_url;
  },
);

// If the user is in a breakout, return the screensharing user for that group.
// Otherwise, return the main class session screensharing user.
export const selectScreensharingClassUserId = createAppSelector(
  [selectActiveClassSession, selectCurrentClassUser, selectBreakoutGroups],
  (classSession, currentClassUser, breakoutGroups) => {
    const breakoutGroupId = currentClassUser?.breakout_group_id;
    if (breakoutGroupId) {
      const breakoutGroup = breakoutGroups.find((bg) => bg.id === breakoutGroupId);
      return breakoutGroup?.screensharing_class_user_id || null;
    }
    return classSession?.screensharing_class_user_id || null;
  },
);

export const selectIsScreensharingOn = createAppSelector(
  [selectScreensharingClassUserId],
  (screensharingClassUserId) => !!screensharingClassUserId,
);

export const selectCanStudentsScreenshare = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.can_students_screenshare || false,
);

export const selectViewingScreenshare = createAppSelector(
  [
    selectIsRecordingAgent,
    selectIsScreensharingOn,
    (state: RootState) => state.view.viewingScreenshare,
  ],
  (isRecordingAgent, isScreensharingOn, viewingScreenshare) =>
    (isRecordingAgent && isScreensharingOn) || viewingScreenshare,
);

export const selectCountStudentsInVirtualClass = createAppSelector(
  [selectUserIdsWhoCanParticipateInCourse, selectUserIdsInVirtualClass],
  (studentUserIds, userIdsInVirtualClass) => {
    return studentUserIds.filter((userId) => userIdsInVirtualClass.includes(userId)).length;
  },
);

export const selectVisibleClassUsersInVirtualClass = createAppSelector(
  [
    selectClassUsersInVirtualClass,
    selectUserIdsWhoAreVisibleInClass,
    selectCourseUserIdToUserIdMap,
  ],
  (classUsersInVirtualClass, visibleUserIds, courseUserIdToUserIdMap) =>
    classUsersInVirtualClass.filter((classUser) => {
      const userId = courseUserIdToUserIdMap.get(classUser.course_user_id);
      if (!userId) {
        return false;
      }
      return visibleUserIds.includes(userId);
    }),
);

export const selectClassUsersInMainCall = createAppSelector(
  [selectVisibleClassUsersInVirtualClass],
  (classUsers) => classUsers.filter((classUser) => !classUser.breakout_group_id),
);
